import { useTranslation } from 'react-i18next'
import { PlanReasons } from './PlanReasons'
import { PreDrawTable } from './PreDrawTable'
import { PlanWeekView } from './PlanWeekView'
import { activeReasons, unmatchedExercises, removeExercise, removeSession } from './planHelpers'

export function PlanReview({ plan, weeks, onWeeksChange, acceptError, isAccepting = false, onAccept, onBack }) {
  const { t } = useTranslation()
  const reasons = activeReasons(plan.reasons, weeks)
  const unmatched = unmatchedExercises(weeks)
  const hasSessions = weeks.some((w) => w.days.length > 0)
  const hasDeload = weeks.some((w) => w.is_deload)
  const notes = [...(plan.guardrail_corrections || []), ...(plan.guardrail_violations || [])]

  return (
    <div className="space-y-4">
      {plan.plan_overview?.description && <p className="text-sm text-gray-700">{plan.plan_overview.description}</p>}
      {weeks.length > 1 && !hasDeload && (
        <p className="text-sm text-amber-700">{t('aiPlan.review.noDeload')}</p>
      )}
      {weeks.length > 1 && hasDeload && (
        <p className="text-sm text-gray-600">
          {t('aiPlan.review.deloadWeeks', { weeks: weeks.filter((w) => w.is_deload).map((w) => w.week_number).join(', ') })}
        </p>
      )}

      <PlanReasons reasons={reasons} findings={plan.findings} />

      {notes.length > 0 && (
        <section aria-labelledby="ai-plan-guardrails">
          <h3 id="ai-plan-guardrails" className="text-sm font-semibold text-gray-900 mb-1">{t('aiPlan.review.guardrails')}</h3>
          {plan.regenerated && <p className="text-xs text-gray-500">{t('aiPlan.review.regenerated')}</p>}
          <ul className="list-disc pl-5 text-sm text-gray-700">
            {notes.map((note, i) => <li key={i}>{note}</li>)}
          </ul>
        </section>
      )}

      <PreDrawTable rows={plan.pre_draw_table} />

      <div className="space-y-3">
        {weeks.map((week) => (
          <PlanWeekView
            key={week.week_number}
            week={week}
            onRemoveExercise={(w, d, i) => onWeeksChange(removeExercise(weeks, w, d, i))}
            onRemoveSession={(w, d) => onWeeksChange(removeSession(weeks, w, d))}
          />
        ))}
      </div>

      {unmatched.length > 0 && (
        <p role="alert" className="text-sm text-red-600">
          {t('aiPlan.review.unmatchedBlock', { n: unmatched.length })}
        </p>
      )}

      {acceptError?.kind === 'conflict' && (
        <div role="alert" className="rounded-md border border-red-200 p-3 text-sm text-red-700">
          <p className="font-medium">{t('aiPlan.errors.conflict')}</p>
          <ul className="list-disc pl-5">
            {acceptError.conflicts.map((c) => (
              <li key={`${c.year}-${c.iso_week}`}>
                {t('aiPlan.errors.conflictWeek', { week: c.week_number, isoWeek: c.iso_week, year: c.year })}
              </li>
            ))}
          </ul>
        </div>
      )}
      {acceptError?.kind === 'invalid' && (
        <div role="alert" className="rounded-md border border-red-200 p-3 text-sm text-red-700">
          <p className="font-medium">{t('aiPlan.errors.invalid')}</p>
          <ul className="list-disc pl-5">
            {acceptError.problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}
      {acceptError?.kind === 'error' && (
        <p role="alert" className="text-sm text-red-600">{t('aiPlan.errors.accept')}</p>
      )}

      <div className="flex justify-between gap-2">
        <button type="button" className="btn-outline" onClick={onBack}>{t('aiPlan.review.back')}</button>
        <button
          type="button"
          className="btn-primary"
          onClick={onAccept}
          disabled={isAccepting || unmatched.length > 0 || !hasSessions}
        >
          {isAccepting ? t('aiPlan.review.accepting') : t('aiPlan.review.accept')}
        </button>
      </div>
    </div>
  )
}
